import { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { getIsPointsPay } from 'store/ui/uiSelectors';
import { POINTS_EARN_ENABLED } from 'config';

// eslint-disable-next-line no-unused-vars
const usePointsEarnedDisabled = ({ pointsEarned }) => ({ onCashPaymentAmountChange: noop, pointsEarned: null });

const usePointsEarnedEnabled = ({ pointsEarned: initialPointsEarned }) => {
  const isPointsPay = useSelector(getIsPointsPay);
  const { propertyPpd, maxQffEarnPpd = 0, maxQbrEarnPpd = 0, qffPoints, qbrPoints } = initialPointsEarned;

  // assign to the minimal attributes we want to expose to prevent use of
  // attributes that won't be available after a recalculation
  const minimalPointsEarned = useMemo(
    () => ({
      qffPoints: {
        base: qffPoints.base,
        total: qffPoints.total,
      },
      qbrPoints: {
        total: qbrPoints.total,
      },
      maxQffEarnPpd,
      propertyPpd,
    }),
    [qffPoints.base, qffPoints.total, qbrPoints.total, maxQffEarnPpd, propertyPpd],
  );

  const [pointsEarned, setPointsEarned] = useState(minimalPointsEarned);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCashPaymentAmountChange = useCallback(
    debounce(({ cashAmount }) => {
      setPointsEarned({
        maxQffEarnPpd,
        qffPoints: {
          base: cashAmount.times(propertyPpd).toNumber(), //propertyPpd contains the default 'price per dollar' multiplier before any campaign is applied
          total: cashAmount.times(maxQffEarnPpd).toNumber(),
        },
        qbrPoints: {
          total: cashAmount.times(maxQbrEarnPpd).toNumber(),
        },
      });
    }, 200),
    [maxQbrEarnPpd, maxQffEarnPpd],
  );

  useEffect(() => {
    if (!isPointsPay) {
      setPointsEarned(minimalPointsEarned);
    }
  }, [minimalPointsEarned, isPointsPay]);

  return { onCashPaymentAmountChange, pointsEarned };
};

const usePointsEarned = POINTS_EARN_ENABLED ? usePointsEarnedEnabled : usePointsEarnedDisabled;

export default usePointsEarned;
